import { CountryMapper } from "@cuauth/country-mapper";

/**
 * Session language key written to `localStorage` (`lang`).
 */
export const LANG_STORAGE_KEY = "lang";

/**
 * Reads the session language from `localStorage.lang`.
 * Returns null when storage is unavailable or empty.
 */
export function getStoredLocale(): string | null {
  try {
    return localStorage.getItem(LANG_STORAGE_KEY);
  } catch {
    return null;
  }
}

export interface ResolveInitialLocaleInput {
  /**
   * Raw `authorization_params.ui_locales` value. When present and supported,
   * its first token establishes the authoritative country.
   */
  uiLocales?: string | null;
  /**
   * Raw `authorization_params.lang` value. Used as a country source only when
   * `ui_locales` is absent or unsupported.
   */
  lang?: string | null;
  /**
   * Optional `localStorage.lang` override. Defaults to the current stored
   * value. May only select a language within the established country.
   */
  storedLocale?: string | null;
}

function toSupportedLocale(
  candidate: string | null | undefined,
): string | null {
  if (!candidate) {
    return null;
  }

  const [firstLocale] = candidate.trim().split(/\s+/);
  const normalised = CountryMapper.normaliseLocale(firstLocale);

  if (!normalised || !CountryMapper.isSupportedLocale(normalised)) {
    return null;
  }

  return normalised;
}

/**
 * Prefer a stored language only when it belongs to the same country as the
 * authorize locale (e.g. CH: de-CH / fr-CH / it-CH).
 */
function applyStoredLocaleOverride(
  authorizeLocale: string,
  storedLocale: string | null | undefined,
): string | null {
  const stored = toSupportedLocale(storedLocale);
  if (!stored) {
    return null;
  }

  const authorizeCountry =
    CountryMapper.parseCountryCodeWhenSupported(authorizeLocale);
  const storedCountry = CountryMapper.parseCountryCodeWhenSupported(stored);

  if (
    !authorizeCountry ||
    !storedCountry ||
    authorizeCountry !== storedCountry
  ) {
    return null;
  }

  if (!CountryMapper.getAvailableLocales(authorizeCountry).includes(stored)) {
    return null;
  }

  return stored;
}

/**
 * Resolves the initial auth locale from Auth0 authorization parameters plus an
 * optional `localStorage.lang` override.
 *
 * Precedence:
 * 1. A supported first `ui_locales` token establishes the country.
 * 2. When `ui_locales` is absent or unsupported, a supported `lang` may
 *    establish the country.
 * 3. `localStorage.lang` may override only the language within that country.
 * 4. When both `ui_locales` and `lang` are supported but target different
 *    countries, the result is null (fail closed).
 * 5. Missing or unresolved input returns null. No country-default locale is
 *    synthesized.
 *
 * `transaction.locale` is intentionally not used.
 */
export function resolveInitialLocale(
  input: ResolveInitialLocaleInput,
): string | null {
  const {
    uiLocales = null,
    lang = null,
    storedLocale = getStoredLocale(),
  } = input;

  const fromUiLocales = toSupportedLocale(uiLocales);
  const fromLang = toSupportedLocale(lang);

  if (fromUiLocales && fromLang) {
    const uiCountry =
      CountryMapper.parseCountryCodeWhenSupported(fromUiLocales);
    const langCountry = CountryMapper.parseCountryCodeWhenSupported(fromLang);

    if (uiCountry && langCountry && uiCountry !== langCountry) {
      return null;
    }
  }

  const authorizeLocale = fromUiLocales ?? fromLang;
  if (!authorizeLocale) {
    return null;
  }

  return (
    applyStoredLocaleOverride(authorizeLocale, storedLocale) ?? authorizeLocale
  );
}
